import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  AlertTriangle, Check, FolderLock, Layers, Link2, Loader, Lock, LockOpen, Pencil, Plus,
  ScrollText, Trash2, Unlink, UserPlus, Users, X,
} from 'lucide-react';
import { PageHeader } from '../components/ui/PageHeader';
import { StatTile } from '../components/ui/StatTile';
import { EmptyState } from '../components/ui/EmptyState';
import { SectionHeader } from '../components/ui/SectionHeader';
import { SkeletonLoader } from '../components/SkeletonLoader';
import { CreatorProfile } from '../components/CreatorProfile';
import { PlayerProfile } from '../components/PlayerProfile';
import { ConfirmationModal } from '../components/ConfirmationModal';
import { LogbookEventList } from '../components/logbook/LogbookEventList';
import { LogbookCopyButton } from '../components/logbook/LogbookCopyButton';
import { LinkRecordModal } from '../components/logbook/LinkRecordModal';
import { PlayerSearchField, type PlayerSelection } from '../components/eventteam/PlayerSearchField';
import { useAuth } from '../contexts/AuthContext';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useLogbookServers } from '../hooks/useLogbookServers';
import { logbookApiService, LogbookApiError } from '../services/logbookApi';
import { canAdministerLogbook, canProtectLogbook } from '../utils/logbookPermissions';
import { formatCount, relativeTime } from '../utils/logbook';
import { groupEvents } from '../utils/logbookView';
import { DEFAULT_LOGBOOK_FILTERS, filtersToParams } from '../utils/logbookFilters';
import { cn } from '../utils/cn';
import type {
  LogbookCaseDetail, LogbookCaseItemType, LogbookCaseSubjectRole, LogbookEvent,
} from '../types/logbook';

/**
 * One case, in full.
 *
 * The evidence here is Argus's own copy, so it renders from the stored record rather than from a
 * fresh read: what a ban was based on stays what it was based on, whatever Logbook holds now.
 *
 * Everything on the page is editable while the case is open and frozen once it is closed, because a
 * closed case is a finished account of a decision rather than a working document.
 */

/**
 * Where a linked record lives. Notes, warnings and bans are rows in a list rather than pages of
 * their own, so they are reached through the highlight parameter that opens and scrolls to the row.
 */
const ITEM_ROUTES: Record<LogbookCaseItemType, (id: string) => string | null> = {
  note: (id) => `/notes-warnings?highlight=${encodeURIComponent(id)}`,
  warning: (id) => `/notes-warnings?highlight=${encodeURIComponent(id)}`,
  ban: (id) => `/bans?highlight=${encodeURIComponent(id)}`,
  incident: (id) => `/incidents/${id}`,
  ticket: () => null,
  appeal: (id) => `/appeals/${id}`,
};

const ITEM_LABELS: Record<LogbookCaseItemType, string> = {
  note: 'Note', warning: 'Warning', ban: 'Ban', incident: 'Incident', ticket: 'Ticket', appeal: 'Appeal',
};

const ROLE_TONE: Record<LogbookCaseSubjectRole, string> = {
  suspect: 'badge-danger',
  affected: 'badge-info',
  witness: 'badge-neutral',
};

export function LogbookCaseDetailView() {
  const { caseId = '' } = useParams<{ caseId: string }>();
  const navigate = useNavigate();
  const { identity } = useAuth();
  const canManage = canProtectLogbook(identity);
  const canDelete = canAdministerLogbook(identity);
  const { byId: serversById } = useLogbookServers();

  const [detail, setDetail] = useState<LogbookCaseDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<LogbookApiError | Error | null>(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState('');
  const [summary, setSummary] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [nonce, setNonce] = useState(0);

  useDocumentTitle(detail ? `${detail.reference} ${detail.title}` : 'Logbook case');

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;
    setLoading(true);
    logbookApiService.getCase(caseId, controller.signal)
      .then((result) => {
        if (cancelled) return;
        setDetail(result);
        setTitle(result.title);
        setSummary(result.summary ?? '');
        setError(null);
      })
      .catch((caught: unknown) => {
        if (cancelled || controller.signal.aborted) return;
        setDetail(null);
        setError(caught instanceof Error ? caught : new Error('That case could not be loaded.'));
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; controller.abort(); };
  }, [caseId, nonce]);

  const open = detail?.status === 'open';
  const editable = canManage && open;

  const runs = useMemo(() => {
    const events = (detail?.records ?? [])
      .map((record) => record.event)
      .filter((event): event is LogbookEvent => Boolean(event));
    return groupEvents(events, 300_000);
  }, [detail]);

  /** Records whose stored copy could not be parsed. Never silently dropped from the count. */
  const unreadable = useMemo(
    () => (detail?.records ?? []).filter((record) => !record.event),
    [detail],
  );

  const act = useCallback(async (operation: () => Promise<unknown>) => {
    setBusy(true);
    try { await operation(); setNonce((value) => value + 1); }
    catch (caught) { setError(caught instanceof Error ? caught : new Error('That change could not be saved.')); }
    finally { setBusy(false); }
  }, []);

  /** The obvious next step from a case: find more of the same actor's evidence. */
  const findEvidenceHref = useMemo(() => {
    const suspect = detail?.subjects.find((subject) => subject.role === 'suspect');
    const params = filtersToParams({
      ...DEFAULT_LOGBOOK_FILTERS,
      windowId: '30d',
      actorId: suspect?.subject_uuid ?? '',
    });
    return `/logbook?${params.toString()}`;
  }, [detail]);

  if (loading && !detail) {
    return (
      <div className="space-y-6">
        <PageHeader eyebrow="Moderation" title="Case" icon={FolderLock} backTo="/logbook/cases" backLabel="Back to cases" />
        <SkeletonLoader className="h-40 w-full rounded-card" />
      </div>
    );
  }

  if (error && !detail) {
    const missing = error instanceof LogbookApiError && error.status === 404;
    return (
      <div className="space-y-6">
        <PageHeader eyebrow="Moderation" title="Case" icon={FolderLock} backTo="/logbook/cases" backLabel="Back to cases" />
        <EmptyState
          icon={missing ? FolderLock : AlertTriangle}
          tone={missing ? 'neutral' : 'danger'}
          title={missing ? 'That case does not exist' : 'The case could not be loaded'}
          description={error.message}
          action={<Link to="/logbook/cases" className="glass-button">Back to cases</Link>}
        />
      </div>
    );
  }

  if (!detail) return null;

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Moderation"
        title={editing ? 'Editing case' : detail.title}
        description={editing ? undefined : detail.summary ?? undefined}
        icon={FolderLock}
        backTo="/logbook/cases"
        backLabel="Back to cases"
        titleAdornment={(
          <>
            <span className={cn('badge', open ? 'badge-success' : 'badge-neutral')}>{detail.status}</span>
            <span className="badge badge-accent font-mono">{detail.reference}</span>
            <LogbookCopyButton value={detail.reference} label="Case reference" />
          </>
        )}
        meta={(
          <>
            <span className="flex items-center gap-1.5">
              opened by <CreatorProfile creatorId={detail.created_by} inline />
              <span className="tabular-nums">{relativeTime(detail.created_at)}</span>
            </span>
            {detail.closed_at && (
              <span className="flex items-center gap-1.5">
                closed by <CreatorProfile creatorId={detail.closed_by ?? ''} inline />
                <span className="tabular-nums">{relativeTime(detail.closed_at)}</span>
              </span>
            )}
          </>
        )}
        actions={(
          <>
            <Link to={findEvidenceHref} className="glass-button">
              <ScrollText className="w-4 h-4" />
              Find evidence
            </Link>
            {canManage && !editing && (
              <button type="button" className="glass-button" onClick={() => setEditing(true)} disabled={!open}>
                <Pencil className="w-4 h-4" />
                Edit
              </button>
            )}
            {canManage && (
              <button
                type="button"
                className={open ? 'glass-button' : 'btn-primary'}
                disabled={busy}
                onClick={() => act(() => logbookApiService.updateCase(detail.id, { status: open ? 'closed' : 'open' }))}
              >
                {busy ? <Loader className="w-4 h-4 animate-spin" /> : open ? <Lock className="w-4 h-4" /> : <LockOpen className="w-4 h-4" />}
                {open ? 'Close case' : 'Reopen'}
              </button>
            )}
            {canDelete && (
              <button type="button" className="btn-danger" onClick={() => setConfirmDelete(true)} disabled={busy}>
                <Trash2 className="w-4 h-4" />
                Delete
              </button>
            )}
          </>
        )}
      />

      {error && detail && (
        <div className="alert alert-danger">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          <span>{error.message}</span>
        </div>
      )}

      {!open && (
        <div className="alert alert-info">
          <Lock className="w-4 h-4 shrink-0" />
          <span>This case is {detail.status}. Reopen it to attach or remove records.</span>
        </div>
      )}

      {editing && (
        <div className="glass-card p-4 md:p-5 space-y-3">
          <SectionHeader title="Case details" icon={Pencil} />
          <label className="block">
            <span className="block text-xs font-medium text-glass-400 mb-1.5">Title</span>
            <input type="text" value={title} onChange={(event) => setTitle(event.target.value)} className="form-input" maxLength={200} />
          </label>
          <label className="block">
            <span className="block text-xs font-medium text-glass-400 mb-1.5">Summary</span>
            <textarea
              value={summary}
              onChange={(event) => setSummary(event.target.value)}
              className="form-input min-h-[96px] resize-y"
              maxLength={4000}
              placeholder="Optional"
            />
          </label>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="btn-primary"
              disabled={busy || !title.trim()}
              onClick={() => act(async () => {
                await logbookApiService.updateCase(detail.id, { title: title.trim(), summary: summary.trim() });
                setEditing(false);
              })}
            >
              {busy ? <Loader className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              Save
            </button>
            <button type="button" className="glass-button-secondary" onClick={() => { setEditing(false); setTitle(detail.title); setSummary(detail.summary ?? ''); }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-3 gap-3">
        <StatTile label="Records held" value={formatCount(detail.record_count)} icon={Layers} tone="accent" />
        <StatTile label="People named" value={formatCount(detail.subject_count)} icon={Users} tone="purple" />
        <StatTile label="Linked records" value={formatCount(detail.link_count)} icon={Link2} tone="info" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <SubjectsCard detail={detail} editable={editable} busy={busy} onAct={act} />
        <LinksCard detail={detail} editable={canManage} busy={busy} onAct={act} />
      </div>

      <div className="glass-card overflow-hidden">
        <div className="p-4 md:p-5 pb-3">
          <SectionHeader
            title="Evidence"
            icon={ScrollText}
            count={detail.record_count}
            actions={editable && (
              <Link to={findEvidenceHref} className="glass-button-sm">
                <Plus className="w-4 h-4" />
                Add more
              </Link>
            )}
          />
          {unreadable.length > 0 && (
            <div className="alert alert-warning">
              <AlertTriangle className="w-4 h-4 shrink-0" />
              <span>{formatCount(unreadable.length)} stored copies could not be read back and are not shown below.</span>
            </div>
          )}
        </div>

        {runs.length === 0 ? (
          <div className="px-4 pb-6">
            <EmptyState
              bare
              compact
              icon={ScrollText}
              title="No evidence attached yet"
              description={editable
                ? 'Search Logbook, then add records to this case.'
                : 'Nothing has been attached to this case.'}
              action={editable && <Link to={findEvidenceHref} className="glass-button"><ScrollText className="w-4 h-4" />Find evidence</Link>}
            />
          </div>
        ) : (
          <LogbookEventList runs={runs} serversById={serversById} />
        )}

        {detail.records.some((record) => record.note) && (
          <div className="p-4 border-t border-glass-200/10 space-y-2">
            <div className="eyebrow">Notes left when attaching</div>
            {detail.records.filter((record) => record.note).map((record) => (
              <div key={record.id} className="flex flex-wrap items-baseline gap-2 text-xs">
                <code className="text-glass-500 font-mono">{record.event_id}</code>
                <span className="text-glass-200">{record.note}</span>
                <span className="text-glass-500 flex items-center gap-1">
                  <CreatorProfile creatorId={record.added_by} inline />
                  <span className="tabular-nums">{relativeTime(record.added_at)}</span>
                </span>
                {editable && (
                  <button
                    type="button"
                    className="btn-icon-sm ml-auto"
                    title="Remove this record from the case"
                    aria-label="Remove this record from the case"
                    disabled={busy}
                    onClick={() => act(() => logbookApiService.detachRecord(detail.id, record.id))}
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {confirmDelete && (
        <ConfirmationModal
          title="Delete this case"
          message={`This destroys the only permanent copy of ${formatCount(detail.record_count)} evidence records. Logbook may no longer hold the originals. This cannot be undone.`}
          confirmText="Delete case"
          onConfirm={async () => {
            setConfirmDelete(false);
            await act(async () => {
              await logbookApiService.deleteCase(detail.id);
              navigate('/logbook/cases');
            });
          }}
          onClose={() => setConfirmDelete(false)}
        />
      )}
    </div>
  );
}

interface CardProps {
  detail: LogbookCaseDetail;
  editable: boolean;
  busy: boolean;
  onAct: (operation: () => Promise<unknown>) => Promise<void>;
}

/** Who the case is about, which is not always who its evidence names. */
const SubjectsCard = ({ detail, editable, busy, onAct }: CardProps) => {
  const [player, setPlayer] = useState<PlayerSelection | null>(null);
  const [role, setRole] = useState<LogbookCaseSubjectRole>('suspect');
  const named = useMemo(() => new Set(detail.subjects.map((subject) => subject.subject_uuid)), [detail.subjects]);
  const duplicate = Boolean(player && named.has(player.uuid));

  return (
    <div className="glass-card p-4 md:p-5">
      <SectionHeader title="People" icon={Users} count={detail.subjects.length || undefined} />

      {detail.subjects.length === 0 ? (
        <p className="text-sm text-glass-500">Nobody named yet.</p>
      ) : (
        <ul className="divide-y divide-glass-200/10">
          {detail.subjects.map((subject) => (
            <li key={subject.id} className="py-2 flex items-center gap-2 min-w-0">
              <span className="flex-1 min-w-0">
                <PlayerProfile uuid={subject.subject_uuid} showUuid={false} />
              </span>
              <span className={cn('badge', ROLE_TONE[subject.role])}>{subject.role}</span>
              {editable && (
                <button
                  type="button"
                  className="btn-icon-sm"
                  title="Remove"
                  aria-label="Remove"
                  disabled={busy}
                  onClick={() => onAct(() => logbookApiService.removeSubject(detail.id, subject.id))}
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {editable && (
        <div className="mt-3 space-y-2">
          {/* Searched by username: the UUID is what the case stores, not what a moderator knows. */}
          <PlayerSearchField value={player} onChange={setPlayer} placeholder="Search a player, or paste a UUID" />
          <div className="flex flex-col sm:flex-row gap-2">
            <select
              value={role}
              onChange={(event) => setRole(event.target.value as LogbookCaseSubjectRole)}
              className="glass-select flex-1 min-w-0"
              aria-label="Role"
            >
              <option value="suspect">Suspect</option>
              <option value="affected">Affected</option>
              <option value="witness">Witness</option>
            </select>
            <button
              type="button"
              className="glass-button shrink-0"
              disabled={!player || duplicate || busy}
              onClick={() => onAct(async () => {
                await logbookApiService.addSubject(detail.id, player!.uuid, role);
                setPlayer(null);
              })}
            >
              <UserPlus className="w-4 h-4" />
              Add
            </button>
          </div>
          {duplicate && <p className="text-xs text-glass-500">This case already names that player.</p>}
        </div>
      )}
    </div>
  );
};

/** What the case justified. Linking stays available on a closed case: a ban can follow the decision. */
const LinksCard = ({ detail, editable, busy, onAct }: CardProps) => {
  const [linking, setLinking] = useState(false);

  return (
    <div className="glass-card p-4 md:p-5">
      <SectionHeader
        title="Attached to"
        icon={Link2}
        count={detail.links.length || undefined}
        actions={editable && (
          <button type="button" className="glass-button-sm" onClick={() => setLinking(true)} disabled={busy}>
            <Plus className="w-4 h-4" />
            Attach a record
          </button>
        )}
      />

      {detail.links.length === 0 ? (
        <p className="text-sm text-glass-500">Not attached to anything yet.</p>
      ) : (
        <ul className="divide-y divide-glass-200/10">
          {detail.links.map((link) => {
            const route = ITEM_ROUTES[link.item_type](link.item_id);
            return (
              <li key={link.id} className="py-2 flex items-center gap-2 min-w-0">
                <span className="badge badge-neutral">{ITEM_LABELS[link.item_type]}</span>
                {route ? (
                  <Link to={route} className="link text-sm font-mono truncate">{link.item_id}</Link>
                ) : (
                  <code className="text-sm text-glass-200 font-mono truncate">{link.item_id}</code>
                )}
                <span className="ml-auto text-xs text-glass-500 tabular-nums shrink-0">{relativeTime(link.linked_at)}</span>
                {editable && (
                  <button
                    type="button"
                    className="btn-icon-sm shrink-0"
                    title="Unlink"
                    aria-label="Unlink"
                    disabled={busy}
                    onClick={() => onAct(() => logbookApiService.unlinkCase(detail.id, link.id))}
                  >
                    <Unlink className="w-3.5 h-3.5" />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {linking && (
        <LinkRecordModal
          caseId={detail.id}
          subjects={detail.subjects.map((subject) => ({ uuid: subject.subject_uuid, role: subject.role }))}
          alreadyLinked={new Set(detail.links.map((link) => `${link.item_type}:${link.item_id}`))}
          onClose={() => setLinking(false)}
          // The link is already written; this only reloads the case around it.
          onLinked={() => { setLinking(false); onAct(async () => undefined); }}
        />
      )}
    </div>
  );
};
